import { useEffect, useState, useMemo } from "react";
import {
  useLazyGetAvailableQtyListQuery,
  useLazyGetRecordAvailableQtyListQuery,
} from "Services/ProductApiSlice";

export type BulkStockItem = string | { item: string; productType?: string };

const isRecordType = (productType?: string) => productType?.toUpperCase() === "RECORD";

export const useBulkStock = (items: BulkStockItem[], skip: boolean = false) => {
  const [availableQtyMap, setAvailableQtyMap] = useState<Record<string, number>>({});
  const [triggerGetAvailableQtyList] = useLazyGetAvailableQtyListQuery();
  const [triggerGetRecordAvailableQtyList] = useLazyGetRecordAvailableQtyListQuery();

  const normalizedItems = useMemo(
    () => items.map((it) => (typeof it === "string" ? { item: it, productType: undefined } : it)),
    [items],
  );

  const regularItemIds = useMemo(
    () => normalizedItems.filter((it) => !isRecordType(it.productType)).map((it) => it.item),
    [normalizedItems],
  );
  const recordItemIds = useMemo(
    () => normalizedItems.filter((it) => isRecordType(it.productType)).map((it) => it.item),
    [normalizedItems],
  );

  const memoizedRegularKey = useMemo(() => regularItemIds.join(","), [regularItemIds]);
  const memoizedRecordKey = useMemo(() => recordItemIds.join(","), [recordItemIds]);

  useEffect(() => {
    if (skip || (!regularItemIds.length && !recordItemIds.length)) {
      setAvailableQtyMap({});
      return;
    }

    const fetchStock = () => {
      const requests: Promise<{ data?: { results?: Array<{ item: string; qty: number }> } }>[] = [];

      if (regularItemIds.length) {
        requests.push(
          triggerGetAvailableQtyList({ items: regularItemIds, origin: window.location.origin }, false),
        );
      }
      if (recordItemIds.length) {
        requests.push(
          triggerGetRecordAvailableQtyList({ items: recordItemIds, origin: window.location.origin }, false),
        );
      }

      Promise.all(requests).then((results) => {
        const map: Record<string, number> = {};
        results.forEach((result) => {
          (result.data?.results ?? []).forEach(({ item, qty }) => {
            map[item] = qty;
          });
        });
        setAvailableQtyMap(map);
      });
    };

    fetchStock();
    const interval = setInterval(fetchStock, 30000);
    return () => clearInterval(interval);
  }, [
    memoizedRegularKey,
    memoizedRecordKey,
    triggerGetAvailableQtyList,
    triggerGetRecordAvailableQtyList,
    skip,
  ]);

  return availableQtyMap;
};
